// cspell:ignore topup topupmalformed transfermalformed instantpaymenttopuprequesttransitionedtomalformed instantpaymenttransferrequesttransitionedtomalformed
import { KalaWalletKey } from '../../../models/wallet.model';
import {
	GatewayDepositOutcome,
	GatewayDepositStage,
	TransferStateKey,
	WalletTransferKind,
	WalletTransferNotice,
	WalletTransferStateMeta,
	WalletTransferStepStatus
} from './wallet-transfer-history.model';

/** The header's title, and the id its back chevron reports. */
export const WALLET_TRANSFER_HISTORY_TITLE = 'تاریخچه تراکنش‌ها';
export const WALLET_TRANSFER_HISTORY_BACK_EVENT_ID = 'back';

/**
 * How each state a record can be in reads on the row's chip.
 *
 * `state` is a plain string in the contract, with no enum behind it, so the values are read
 * into one of these keys by spelling rather than compared one by one — the events endpoint
 * spells the same states inside `TransferRequestTransitionedTo…` and `InstantPayment…`
 * event names, and both go through the same reader.
 */
export const TRANSFER_STATE_METAS: Record<TransferStateKey, WalletTransferStateMeta> = {
	pending: {
		label: 'درحال انجام',
		icon: 'sandglass',
		className: 'text-yellow-500',
		tip: 'yellow'
	},
	completed: {
		label: 'انجام شد',
		icon: 'transaction-check',
		className: 'text-green-600',
		tip: 'green'
	},
	failed: {
		label: 'در حال انجام',
		icon: 'sandglass',
		className: 'text-yellow-500',
		tip: 'yellow'
	},
	malformed: {
		label: 'خطا در تراکنش',
		icon: 'transaction-error',
		className: 'text-red-500',
		tip: 'red'
	},
	canceled: {
		label: 'لغو شد',
		icon: 'transaction-close',
		className: 'text-gray-500',
		tip: 'mute'
	}
};

/**
 * How a record reads in the `MainWalletRequestSent` state (as `latest-active` reports it): the
 * request has left for بورس کالا and the answer is not back yet. Still in flight, so its
 * {@link TransferStateKey} stays `pending`.
 */
export const TRANSFER_SENT_TO_KALA_STATE_META: WalletTransferStateMeta = {
	label: 'ارسال به بورس کالا',
	icon: 'sandglass',
	className: 'text-mofid-700',
	tip: 'mofid'
};

/** The events that send a request to بورس کالا, keyed the way {@link TRANSFER_EVENT_STEP_TITLES} is. */
export const TRANSFER_SENT_EVENTS: ReadonlySet<string> = new Set([
	'mainwallettransferrequestsent',
	'mainwalletrequestsent'
]);

/** That state, spelled the way {@link transferStateMeta} reads it. */
export const TRANSFER_SENT_TO_KALA_STATE = 'mainwalletrequestsent';

/**
 * What each event a record has taken says on the bottom sheet, in the documentation's own
 * words. Keyed by the event type with its casing and punctuation stripped.
 */

export const TRANSFER_EVENT_STEP_TITLES: Readonly<Record<string, string>> = {
	transferrequestcreated: 'ثبت درخواست',
	transferrequesttransitionedtopending: 'ایجاد درخواست انتقال',
	transferrequesttransitionedtoapplying: 'پردازش درخواست',
	hamitransferrequestcreated: 'نامشخص',
	mainwallettransferrequestconfirmed: 'تایید انتقال کیف اصلی',
	mainwallettransferrequestsent: 'درخواست انتقال ارسال شد',
	mainwalletrequestsent: 'درخواست انتقال ارسال شد',
	transferrequestcompleted: 'تراکنش با موفقیت انجام شد',
	transferrequesttransitionedtofailed: 'ناموفق',
	transferrequesttransitionedtomalformed: 'انتقال وجه به کیف مقصد با خطا مواجه شد',
	instantpaymenttopuprequesttransitionedtomalformed: 'واریز پول به کیف اصلی با خطا مواجه شد',
	instantpaymenttransferrequesttransitionedtomalformed: 'واریز پول به کیف مقصد با خطا مواجه شد',
	transferrequesttransitionedtocancel: 'لغو شد',
	instantpaymentcreated: 'ثبت پرداخت',
	instantpaymentqueuedforverification: 'در انتظار تایید',
	instantpaymentverified: 'تایید شد',
	instantpaymentqueuedforfinalizing: 'در انتظار نهایی‌سازی',
	instantpaymenttransitionedtofinalizing: 'در حال نهایی‌سازی',
	instantpaymentfinalized: 'نهایی شد',
	instantpaymentfailed: 'ناموفق',
	instantpaymentmaximumattemptsreached: 'حداکثر تلاش انجام شد',
	instantpaymentmarkedasinvalid: 'نامعتبر',
	instantpaymentcancelled: 'لغو شد'
};

/** The bottom sheet's heading per kind, in the handoff's own words. */
export const WALLET_TRANSFER_TIMELINE_TITLES: Record<WalletTransferKind, string> = {
	depositGateway: 'واریز از درگاه بانکی',
	depositBroker: 'واریز از حساب سامانه سهام',
	depositWallet: 'واریز از سایر کیف‌ها',
	withdrawBank: 'برداشت به حساب بانکی',
	withdrawBroker: 'برداشت به حساب سهامی',
	withdrawWallet: 'برداشت به سایر کیف‌ها'
};

/**
 * How a gateway deposit reads as a whole, on the row's chip and on the sheet. A deposit the
 * gateway took but a later stage stopped reads «خطا در تراکنش» on the stage it stopped at, and
 * carries no retry — support finishes it by hand.
 */
export const GATEWAY_DEPOSIT_OUTCOME_METAS: Record<GatewayDepositOutcome, WalletTransferStateMeta> = {
	completed: TRANSFER_STATE_METAS.completed,
	inProgress: {
		label: 'در حال انجام',
		icon: 'sandglass',
		className: 'text-yellow-500',
		tip: 'yellow'
	},
	incomplete: TRANSFER_STATE_METAS.malformed,
	depositFailed: {
		label: 'خطا در واریز',
		icon: 'alert',
		className: 'text-red-500',
		tip: 'red'
	}
};

/**
 * What the sheet says once the gateway took the money but a later stage stopped, its automatic
 * retries spent: charging کیف اصلی, or the move from it into the target wallet.
 */
export const GATEWAY_DEPOSIT_STOPPED_NOTICE: WalletTransferNotice = {
	title: 'خطای انتقال به بورس کالا',
	description:
		'واریز شما موفق بوده است اما انتقال آن به کیف پول بورس کالا با خطا مواجه شده است. در صورتی که تا ۲۴ ساعت آینده، انتقال انجام نشد، پول به حساب شما در کارگزاری بازگردانده خواهد شد'
};

/** A gateway deposit's stages, in the order the timeline draws them. */
export const GATEWAY_DEPOSIT_STAGES: readonly GatewayDepositStage[] = ['gateway', 'chargeMain', 'transfer'];

/**
 * The states a gateway deposit's record is in once the gateway has taken the money and
 * کیف اصلی is charged — waiting on the move into the target wallet, making it, or between
 * two of its retries — spelled the way {@link gatewayDepositProgress} reads them.
 */
export const GATEWAY_DEPOSIT_CHARGED_STATES: ReadonlySet<string> = new Set(['pending', 'applying', 'failed']);

/** The gateway deposit's own first stop: the bank's gateway taking the payment. */
export const TRANSFER_GATEWAY_STEP_TITLE = 'درگاه بانکی';

/**
 * How the gateway's stop reads once it has settled: a success once کیف اصلی is charged
 * (`TransferRequestTransitionedToPending` and on), a failure once the gateway refused it.
 */
export const TRANSFER_GATEWAY_SETTLED_STEP_TITLES: Record<'done' | 'failed', string> = {
	done: 'واریز موفق',
	failed: 'واریز ناموفق'
};

/**
 * The events that move a gateway deposit's stages, keyed by their bare class name lowercased
 * — see {@link eventName}.
 */
export const GATEWAY_DEPOSIT_EVENTS = {
	created: 'transferrequestcreated',
	pending: 'transferrequesttransitionedtopending',
	applying: 'transferrequesttransitionedtoapplying',
	transferFailed: 'transferrequesttransitionedtofailed',
	completed: 'transferrequestcompleted',
	malformed: 'transferrequesttransitionedtomalformed',
	topupMalformed: 'instantpaymenttopuprequesttransitionedtomalformed',
	transferMalformed: 'instantpaymenttransferrequesttransitionedtomalformed'
} as const;

/** The stop every one of the handoff's timelines begins with. */
export const TRANSFER_REQUEST_STEP_TITLE = 'ثبت درخواست';

/** گاه واریز — the money is on its way into کیف اصلی from the gateway or the سهام system. */
export const TRANSFER_CHARGE_MAIN_STEP_TITLE = 'شارژ کیف اصلی';

/** گاه برداشت — the money is gathered out of its own wallet first. */
export const TRANSFER_TO_MAIN_STEP_TITLE = 'انتقال به کیف اصلی';

/** The two ways a withdrawal leaves the exchange, as their last stop reads. */
export const TRANSFER_BANK_STEP_TITLE = 'درخواست از کیف اصلی به مقصد حساب بانکی';
export const TRANSFER_BROKER_STEP_TITLE = 'انتقال به حساب سهامی';

/**
 * The wallets as the timeline names them in a step — «کیف گواهی سپرده», where the card
 * on the کیف پول tab calls the same wallet «کیف پول گواهی سپرده».
 */
export const TRANSFER_WALLET_TITLES: Record<KalaWalletKey | 'Hami', string> = {
	Main: 'کیف اصلی',
	Option: 'کیف اختیار معامله',
	CDC: 'کیف گواهی سپرده',
	Future: 'کیف آتی',
	WareHouse: 'کیف انبار',
	Hami: 'حساب سهام'
};

/**
 * The whole class list each step is drawn with, layout included: the sheet binds `[class]`
 * to one of these outright, the way it must — a static `class` beside a `[class]` binding
 * is the same attribute twice, and the template linter refuses it.
 */
const STEP_CIRCLE = 'z-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full';
const STEP_TITLE = 'text-left text-3.25 font-medium';
const STEP_LINE = 'absolute inset-s-[7.5px] top-5 bottom-1 border-s';

/** The circle a step carries: a check behind, a ring on the one the money waits on. */
export const TRANSFER_STEP_CIRCLE_CLASSES: Record<WalletTransferStepStatus, string> = {
	done: `${STEP_CIRCLE} bg-mofid-700 text-white`,
	current: `${STEP_CIRCLE} border border-blue-700 bg-gray-000`,
	failed: `${STEP_CIRCLE} bg-red-500 text-white`,
	upcoming: `${STEP_CIRCLE} border border-gray-300 bg-gray-000`
};

/** How a step's title reads: the ones behind it in the handoff's blue, the one ahead dark. */
export const TRANSFER_STEP_TITLE_CLASSES: Record<WalletTransferStepStatus, string> = {
	done: `${STEP_TITLE} text-mofid-700`,
	current: `${STEP_TITLE} text-gray-900`,
	failed: `${STEP_TITLE} text-red-500`,
	upcoming: `${STEP_TITLE} text-gray-500`
};

/** The line down from a step to the next: red out of the one the transfer stopped at. */
export const TRANSFER_STEP_LINE_CLASSES: Record<WalletTransferStepStatus, string> = {
	done: `${STEP_LINE} border-blue-700`,
	current: `${STEP_LINE} border-blue-700`,
	failed: `${STEP_LINE} border-red-500`,
	upcoming: `${STEP_LINE} border-gray-300`
};

// Jalali calendar with Latin digits; built once since `Intl.DateTimeFormat` construction is costly.
export const TRANSFER_DATE_FORMAT = new Intl.DateTimeFormat('fa-IR-u-nu-latn', { dateStyle: 'short' });
export const TRANSFER_TIME_FORMAT = new Intl.DateTimeFormat('fa-IR-u-nu-latn', { timeStyle: 'short' });
